import type { BoardData } from '../model/schema.js';
import { BoardData as BoardDataSchema, SCHEMA_VERSION } from '../model/schema.js';
import { createInkLayer, INK_LAYER_ID } from '../model/layers.js';
import { splitLayers } from '../model/layerSplit.js';
import { itemTidyChanges } from '../model/itemTidy.js';
import { trayItemChanges } from '../model/areas.js';

/** Upgrades a raw document from version `n` to `n + 1`. */
export type Migration = (data: Record<string, unknown>) => Record<string, unknown>;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * v1 → v2 (ink layer, decisions I1–I12): adds the `ink` collection and the built-in "Ink" layer
 * on top of all existing layers.
 */
export const addInk: Migration = (data) => {
  const layers = isRecord(data['layers']) ? { ...data['layers'] } : {};
  if (!(INK_LAYER_ID in layers)) {
    const orders = Object.values(layers).map((l) =>
      isRecord(l) && typeof l['order'] === 'number' ? l['order'] : -1,
    );
    layers[INK_LAYER_ID] = createInkLayer(Math.max(-1, ...orders) + 1);
  }
  return { ...data, layers, ink: isRecord(data['ink']) ? data['ink'] : {} };
};

/**
 * v2 → v3 (OIL-51): separate layers for stands, risers and sub-cores (see `layerSplitChanges`).
 */
export const splitCombinedLayers: Migration = (data) => {
  const layers = isRecord(data['layers']) ? (data['layers'] as BoardData['layers']) : {};
  const items = isRecord(data['items']) ? (data['items'] as BoardData['items']) : {};
  const next = splitLayers({ ...(data as unknown as BoardData), layers, items });
  return { ...data, layers: next.layers, items: next.items };
};

/** v3 → v4 (OIL-68, OIL-81): unique item labels, stale catalog references dropped. */
export const tidyItems: Migration = (data) => {
  const items = isRecord(data['items']) ? (data['items'] as BoardData['items']) : {};
  const changes = itemTidyChanges(items);
  return { ...data, items: { ...items, ...Object.fromEntries(changes.map((i) => [i.id, i])) } };
};

/**
 * v4 → v5 (OIL-281): the tray is an explicit `unplaced` flag – `frameId: null` alone now means
 * "free in the world". Exactly the old tray items get the flag; everything else is untouched.
 */
export const trayUnplaced: Migration = (data) => {
  const items = isRecord(data['items']) ? (data['items'] as BoardData['items']) : {};
  const changes = trayItemChanges(items);
  return { ...data, items: { ...items, ...Object.fromEntries(changes.map((i) => [i.id, i])) } };
};

/** Migrations keyed by the version they upgrade *from*. */
export const MIGRATIONS: Readonly<Record<number, Migration>> = {
  1: addInk,
  2: splitCombinedLayers,
  3: tidyItems,
  4: trayUnplaced,
};

export class MigrationError extends Error {
  override name = 'MigrationError';
}

function versionOf(data: Record<string, unknown>): number {
  const meta = data['meta'];
  const v =
    typeof meta === 'object' && meta !== null
      ? (meta as Record<string, unknown>)['schemaVersion']
      : undefined;
  return typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : 1;
}

/**
 * Brings a raw board document up to `target` (default {@link SCHEMA_VERSION}) by applying the
 * migrations in order, sets `meta.schemaVersion` and validates the result.
 * Throws {@link MigrationError} for documents newer than `target` or a missing migration step.
 */
export function migrate(
  data: unknown,
  migrations: Readonly<Record<number, Migration>> = MIGRATIONS,
  target: number = SCHEMA_VERSION,
): BoardData {
  if (typeof data !== 'object' || data === null)
    throw new MigrationError('Document must be an object');
  let doc = data as Record<string, unknown>;
  let version = versionOf(doc);
  if (version > target)
    throw new MigrationError(`Document version ${version} is newer than supported ${target}`);
  while (version < target) {
    const step = migrations[version];
    if (step === undefined) throw new MigrationError(`No migration from version ${version}`);
    doc = step(doc);
    version++;
  }
  const meta = { ...(doc['meta'] as object), schemaVersion: target };
  return BoardDataSchema.parse({ ...doc, meta });
}
